import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  UncontrolledTooltip
} from "reactstrap";
import { Link } from "react-router-dom";
import TableContainer from "../../../../components/Common/TableContainer";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import getAccessRights from "components/Common/getAccessRights";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from 'sweetalert2';
import CustomSelect from "components/Common/custom-select";
import CustomDatePicker from "common/CustomDatePicker";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { changeSalaryScaleStatus, fetchSalaryScale, saveSalaryScale } from "store/actions";
import Loader from "common/pageLoader";
import { convertToInputDate } from "common/dateFormat/formatDate";
import CustomMonthPicker from "common/CustomMonthPicker";

const PayrollSalarySetup = () => {
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);

  const {
    salary_scale,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    salary_scale: state.PayrollSetupSalaryScaleReducer?.salary_scale ?? [],

    loading: state.PayrollSetupSalaryScaleReducer?.loading,
    error: state.PayrollSetupSalaryScaleReducer?.error,
    success: state.PayrollSetupSalaryScaleReducer?.success,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchSalaryScale(unitId));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      dispatch(fetchSalaryScale(authDetails?.unit_id));
      formik.resetForm();
      setIsEdit(false);
      setEditId(null);
    }
  }, [authDetails, success, dispatch]);

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    const accessRight = getAccessRights(location.pathname, history);
  }, [location]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      salaryMonth: null, // Date
      fromDate: null, // Date
      toDate: null, // Date
      attendanceLock: false, // Checkbox
      createdby: authDetails?.user_id,
    },
    validationSchema: Yup.object({
      salaryMonth: Yup.date().nullable().required("Salary month is required").typeError("Invalid date format"),
      fromDate: Yup.date().nullable().required("From date is required").typeError("Invalid date format").when("salaryMonth", (salaryMonth, schema) => {
        if (!salaryMonth) return schema;
        const lastDay = new Date(new Date(salaryMonth).getFullYear(), new Date(salaryMonth).getMonth() + 1, 0);
        return schema.max(lastDay, "Future date is not allowed for selected salary month");
      }),
      toDate: Yup.date().nullable().required("To date is required").typeError("Invalid date format").when("salaryMonth", (salaryMonth, schema) => {
        if (!salaryMonth) return schema;
        const lastDay = new Date(new Date(salaryMonth).getFullYear(), new Date(salaryMonth).getMonth() + 1, 0);
        return schema.max(lastDay, "Future date is not allowed for selected salary month");
      }),
    }),
    onSubmit: (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("scale_id", editId ? editId : 0);
      formData.append("salary_month", values.salaryMonth);
      formData.append("from_date", values.fromDate);
      formData.append("to_date", values.toDate);
      formData.append("is_attendlock", values.attendanceLock ? 1 : 0);
      formData.append("unit_id", authDetails?.unit_id);
      formData.append("create_by", authDetails?.user_id);
      dispatch(saveSalaryScale(formData))
    },
  });


  const handleEdit = (row) => {
    formik.setValues({
      salaryMonth: convertToInputDate(row.sal_month),
      fromDate: convertToInputDate(row.from_date),
      toDate: convertToInputDate(row.to_date),
      attendanceLock: row.attend_lock === 1,
      createdby: authDetails?.user_id,
    });
    setIsEdit(true);
    setEditId(row.salary_id);
  };

  const handleCancel = () => {
    formik.resetForm();
    setIsEdit(false);
    setEditId(null);
  };

  const columns = [
    {
      Header: "S#",
      Cell: ({ row }) => salary_scale.length - row.index,
      className: "serial-no text-center",
    },
    {
      Header: "SALARY_MONTH",
      accessor: "sal_month",
    },
    {
      Header: "FROM_DATE",
      accessor: "from_date",
    },
    {
      Header: "TO_DATE",
      accessor: "to_date",
    },
    {
      Header: "ATTENDANCE_LOCK",
      accessor: row => (row.attend_lock === 1 ? "Yes" : "No"),
    },
    {
      Header: "CREATED_BY",
      accessor: "create_by",
    },
    {
      Header: "CREATED_DATE",
      accessor: "create_dt",
    },
    {
      Header: "ACTIONS",
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (cellProps) => {
        return (
          <div className="text-center">
            {/* Edit Button */}
            <Link
              to="#"
              className="text-success"
              onClick={() => handleEdit(cellProps.row.original)}
            >
              <i className="bx bxs-edit font-size-18" id="edittooltip" />
              <UncontrolledTooltip placement="top" target="edittooltip">
                Edit
              </UncontrolledTooltip>
            </Link>
          </div>
        );
      },
    },
  ];

  const isAttendanceLockAllowed = (salaryMonth) => {
    if (!salaryMonth) return false;

    const selected = new Date(salaryMonth);
    const current = new Date();

    const selectedMonth = new Date(selected.getFullYear(), selected.getMonth(), 1);
    const currentMonth = new Date(current.getFullYear(), current.getMonth(), 1);
    const pastLimit = new Date(current.getFullYear(), current.getMonth() - 6, 1); // allow last 11 months
    return selectedMonth <= currentMonth && selectedMonth >= pastLimit;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Loader loading={loading} />
        <Container fluid>
          {/* Breadcrumbs */}
          <Breadcrumbs title="Payroll / Setup" breadcrumbItem="Salary Setup" />

          {/* Input Section */}
          <Card>
            <CardBody>
              <Form onSubmit={formik.handleSubmit}>
                <Row className="mb-3 g-3 align-items-center">
                  {/* Salary Month */}
                  <Col md="3">
                    <FormGroup>
                      <Label for="salaryMonth">Salary Month <span className="text-danger">*</span></Label>
                      <CustomMonthPicker
                        name="salaryMonth"
                        value={formik.values.salaryMonth}
                        onChange={(date) => {
                          formik.setFieldValue("salaryMonth", date || null);
                          if (!isAttendanceLockAllowed(date)) {
                            formik.setFieldValue("attendanceLock", false);
                          }
                        }}
                        onBlur={() => formik.setFieldTouched("salaryMonth", true)}
                        className={`form-control ${formik.touched.salaryMonth && formik.errors.salaryMonth ? "is-invalid" : ""}`}
                      />
                      {formik.touched.salaryMonth && formik.errors.salaryMonth && (
                        <div className="text-danger">{formik.errors.salaryMonth}</div>
                      )}
                    </FormGroup>
                  </Col>

                  {/* From Date */}
                  <Col md="3">
                    <FormGroup>
                      <Label for="fromDate">From Date <span className="text-danger">*</span></Label>
                      <CustomDatePicker
                        value={formik.values.fromDate}
                        onChange={(date) => formik.setFieldValue("fromDate", date || null)}
                        onBlur={() => formik.setFieldTouched("fromDate", true)}
                      />
                      {formik.touched.fromDate && formik.errors.fromDate && (
                        <div className="text-danger">{formik.errors.fromDate}</div>
                      )}
                    </FormGroup>
                  </Col>

                  {/* To Date */}
                  <Col md="3">
                    <FormGroup>
                      <Label for="toDate">To Date <span className="text-danger">*</span></Label>
                      <CustomDatePicker
                        value={formik.values.toDate}
                        onChange={(date) => formik.setFieldValue("toDate", date || null)}
                        onBlur={() => formik.setFieldTouched("toDate", true)}
                      />
                      {formik.touched.toDate && formik.errors.toDate && (
                        <div className="text-danger">{formik.errors.toDate}</div>
                      )}
                    </FormGroup>
                  </Col>

                  {/* Attendance Lock */}
                  <Col md="3" className="d-flex align-items-center justify-content-center">
                    <FormGroup check className="d-flex align-items-center">
                      <Label check className="gap-2">
                        <Input
                          type="checkbox"
                          name="attendanceLock"
                          checked={formik.values.attendanceLock}
                          onChange={formik.handleChange}
                          disabled={!isAttendanceLockAllowed(formik.values.salaryMonth)}
                        />
                        Attendance Lock
                      </Label>
                    </FormGroup>
                  </Col>

                  {/* Buttons */}
                  <Col md="12" className="d-flex justify-content-end mt-3">
                    <Button type="submit" className="btn btn-primary me-2 mt-2">
                      <i className="fas fa-save me-1" /> {isEdit ? "UPDATE" : "SAVE"}
                    </Button>
                    <Button
                      type="button"
                      color="secondary"
                      className="btn button-cancel me-2 mt-2"
                      onClick={() => {
                        Swal.fire({
                          title: "Are you sure?", text: "Unsaved clear will be lost!", icon: "warning",
                          showCancelButton: true, confirmButtonText: "Yes, Clear", cancelButtonText: "No", confirmButtonColor: "#3085d6", cancelButtonColor: "#d33"
                        }).then((result) => {
                          if (result.isConfirmed) {
                            //  formik.resetForm 
                            handleCancel();
                          }
                        });
                      }}
                    >
                      <i className="fas fa-sync me-1" /> CANCEL
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>

          {/* Table Section */}
          <Card>
            <CardBody>
              <TableContainer
                columns={columns}
                data={salary_scale && salary_scale?.length > 0 ? salary_scale : []}
                isGlobalFilter={true}
                customPageSize={10}
              // className="custom-header-css"
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PayrollSalarySetup;
